import { useMemo, useState, useEffect } from 'react'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { Box, Text, Grid, GridItem, FormLabel } from '@chakra-ui/react'
import { Checkbox, Button, VStack } from '@chakra-ui/react'
import { Formik, Form as FormikForm } from 'formik'
import { useHistory } from 'react-router-dom'
import { RoutePaths } from 'containers/Core/Routes'
import { useOnboardingContext } from 'context/OnboardingContext/OnboardingProvider'
import { usePromoCodeContext } from 'context/PromoCodeContext/PromoCodeProvider'
import usePayment from 'hooks/usePayment/usePayment'
import { useAlert } from 'context/AlertContext/AlertContextProvider'
import { PROGRAMS } from 'utils/program.utils'
import { useStripePayment } from 'hooks/usePayment/useStripePayment'
import { FormInputs } from 'components/Form'
import CheckoutDetails from './CheckoutDetails'
import LoadingState from 'components/Spinner'
import { Logger } from 'utils/logger.utils'
import useUser from 'hooks/useUser/useUser'
import { Amplitude } from 'utils/amplitude.utils'

export interface PaymentFormValues {
  firstName: string
  lastName: string
  paymentInformationConsent: boolean
}

interface CheckoutFormProps {
  isDirectFlow?: boolean
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ isDirectFlow }) => {
  const logger = new Logger('CheckoutForm')
  const { user } = useUser()
  const startTime = useMemo(() => Date.now(), [])

  const stripe = useStripe()
  const history = useHistory()
  const elements = useElements()
  const { setAlertText } = useAlert()
  const [isProcessing, setIsProcessing] = useState(false)
  const { promoCode } = usePromoCodeContext()
  const {
    markPaymentComplete,
    selectedProgram,
    selectedPlan,
    variantLabel,
  } = useOnboardingContext()
  const { attachPaymentMethod, createPaymentIntent } = usePayment()
  const { processCardPayment } = useStripePayment()

  const checkBoxText = useMemo(() => {
    if (
      selectedProgram === PROGRAMS.INSURANCE ||
      selectedProgram === PROGRAMS.INSURANCE_DIAGNOSTIC
    ) {
      return `By checking this box you acknowledge that your payment information will be kept on file for outstanding charges such as copays or coinsurance${
        selectedProgram === PROGRAMS.INSURANCE
          ? ' and for subscription auto-renewal.'
          : '.'
      }`
    } else {
      return `By checking this box you acknowledge that your payment information will be kept on file for outstanding charges${
        selectedProgram !== PROGRAMS.MNC
          ? ' and for subscription auto-renewal.'
          : '.'
      }`
    }
  }, [selectedProgram])

  const handleSubmit = async (values: {
    acceptedTerms: boolean
    firstName: string
    lastName: string
  }) => {
    if (!stripe || !elements) {
      logger.error(
        `selectedPlan:${selectedPlan?.id}:userId:${user?.id}:email:${user?.email}::Stripe or elements not found`
      )
      return
    }

    setIsProcessing(true)

    try {
      if (!selectedPlan) {
        throw new Error('No plan selected')
      }

      // Track checkout initiation
      Amplitude.initiatesCheckout('checkout', selectedPlan.id, variantLabel)

      // Create payment intent with promo code if available
      const paymentIntentResponse = await createPaymentIntent(
        selectedPlan,
        promoCode?.code,
        isDirectFlow
      ).catch(({ error }) => {
        logger.error(
          `Payment intent creation failed: ${selectedPlan}::${error}::${error.request_id}::${error.error_message}::${error.stack}::userId:${user?.id}::email:${user?.email}`
        )
        // These errors are shown to the user, so let's make them a bit obfuscated
        throw new Error('Payment error #01: ' + error)
      })

      if (!paymentIntentResponse?.client_secret) {
        logger.error(
          `Payment intent creation didn't create a client secret: ${selectedPlan}::userId:${user?.id}::email:${user?.email}`
        )
        throw new Error('Payment intent creation error #02')
      }

      // Confirm the setup (creates payment method)
      const { error: setupError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-complete`,
          payment_method_data: {
            billing_details: {
              name: `${values.firstName} ${values.lastName}`,
            },
          },
        },
        redirect: 'if_required',
      })

      if (setupError) {
        logger.error(
          `Stripe setupError on confirmSetup - message:${setupError.message}::type:${setupError.type}::charge:${setupError.charge}::code:${setupError.code}::decline_code:${setupError.decline_code}`
        )
        throw new Error(setupError.message)
      }

      if (!setupIntent?.payment_method) {
        throw new Error('Payment method config error #03')
      }

      // Track when user actually clicks submit to enter payment
      Amplitude.userEntersPayment(
        Math.round((Date.now() - startTime) / 1000),
        variantLabel
      )
      // Create subscription with the new payment method
      await attachPaymentMethod(setupIntent.payment_method as string)

      const paymentSuccess = await processCardPayment(
        paymentIntentResponse.client_secret,
        setupIntent.payment_method as string
      )
        .then(async () => {
          // Wait for payment completion to be marked
          await markPaymentComplete()
          return true
        })
        .catch((error: { error: string }) => {
          logger.error(
            `Payment processing errored out: ${selectedPlan}::${error.error}::userId:${user?.id}::email:${user?.email}`
          )
          throw new Error('Payment error #04: ' + error.error)
        })

      if (!paymentSuccess) {
        logger.error(
          `Payment processing was not successful: ${selectedPlan}::userId:${user?.id}::email:${user?.email}`
        )
        throw new Error('Payment error #05: Processing failed.')
      }

      // Navigate to payment complete page
      history.push(`${RoutePaths.PAYMENT_COMPLETE}`)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred'
      setAlertText(errorMessage, 'Payment Error')
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <Formik
      initialValues={{
        acceptedTerms: false,
        firstName: '',
        lastName: '',
      }}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue }) => (
        <FormikForm>
          <Grid
            templateColumns={{ base: '1fr', md: '1fr 1fr' }}
            gap={8}
            width="100%"
            maxW="6xl"
            mx="auto"
            alignItems="stretch"
            height="100%"
          >
            {/* Left column */}
            <Box
              p={[2, 3, 4]}
              bg="white"
              borderRadius="md"
              width="100%"
              height="100%"
              mx="auto"
            >
              <VStack spacing={4} align="stretch" height="100%">
                <Text fontSize="2xl" fontWeight="bold" mb={4} color="black">
                  Payment Details
                </Text>
                <Grid
                  templateColumns={{ base: '1fr 1fr', md: '1fr 1fr' }}
                  gap={6}
                >
                  <GridItem>
                    <FormInputs
                      htmlFor="firstName"
                      label="First Name"
                      inputtype="text"
                      id="firstName"
                      value={values.firstName}
                      onChange={(e) =>
                        setFieldValue('firstName', e.target.value)
                      }
                    />
                  </GridItem>
                  <GridItem>
                    <FormInputs
                      htmlFor="lastName"
                      label="Last Name"
                      inputtype="text"
                      id="lastName"
                      value={values.lastName}
                      onChange={(e) =>
                        setFieldValue('lastName', e.target.value)
                      }
                    />
                  </GridItem>
                </Grid>
                <Box>
                  <FormLabel fontWeight={300} color={'var(--gray-dark)'} mb={2}>
                    Card Information
                  </FormLabel>
                  <PaymentElement
                    options={{
                      layout: 'tabs',
                      wallets: {
                        applePay: 'auto',
                        googlePay: 'auto',
                      },
                      defaultValues: {
                        billingDetails: {
                          name: `${values.firstName} ${values.lastName}`,
                        },
                      },
                    }}
                  />
                </Box>
              </VStack>
            </Box>

            {/* Right column */}
            <Box
              p={[2, 3, 4]}
              bg="#E8EFF9"
              borderRadius="md"
              width="100%"
              height="100%"
              color="black"
              mx="auto"
              display="flex"
              flexDirection="column"
            >
              <VStack spacing={4} align="stretch">
                <Text fontSize="2xl" fontWeight="bold" mb={4} color="black">
                  Summary
                </Text>
                <CheckoutDetails />
              </VStack>
              <Box mt="auto" pt={4}>
                <Checkbox
                  isChecked={values.acceptedTerms}
                  onChange={(e) =>
                    setFieldValue('acceptedTerms', e.target.checked)
                  }
                  size="lg"
                  colorScheme="blue"
                  alignItems="flex-start"
                  sx={{
                    '.chakra-checkbox__control': {
                      background: 'white',
                      marginTop: '4px',
                      borderColor: 'var(--blue-dark)',
                      _checked: {
                        background: 'var(--blue-dark)',
                        borderColor: 'var(--blue-dark)',
                      },
                    },
                  }}
                >
                  {checkBoxText}
                </Checkbox>
                <Button
                  type="submit"
                  backgroundColor="var(--blue-dark)"
                  color="white"
                  _hover={{
                    backgroundColor: 'var(--blue-dark-hover)',
                    color: 'white',
                  }}
                  isLoading={isProcessing}
                  isDisabled={
                    !stripe ||
                    isProcessing ||
                    !values.acceptedTerms ||
                    !values.firstName ||
                    !values.lastName
                  }
                  width={['100%']}
                  mt={4}
                  mb={4}
                  borderRadius="lg"
                  fontSize="xl"
                  fontWeight="bold"
                >
                  {isProcessing ? <LoadingState /> : 'Submit'}
                </Button>
              </Box>
            </Box>
          </Grid>
        </FormikForm>
      )}
    </Formik>
  )
}

export default CheckoutForm
